/** @jsx jsx */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Icon, Text } from '@chakra-ui/core';
import { jsx, keyframes } from '@emotion/core';

export const QUOTE_PROGRESS_STAGES = {
  diagnostic: 'Diagnostic à domicile',
  projectValidation: 'Constitution et validation du projet',
  funding: 'Instruction financière',
  projectFollowUp: 'Suivi des travaux et solde',
  completion: 'Aboutissement du projet',
};

export const AMO_C_STAGES = Object.values(QUOTE_PROGRESS_STAGES);
export const AMO_AD_STAGES = [
  QUOTE_PROGRESS_STAGES.diagnostic,
  QUOTE_PROGRESS_STAGES.projectValidation,
  QUOTE_PROGRESS_STAGES.completion,
];

const EMPTY_DISABLED_STAGES = [];

const CALCULATED_STATE_TO_STAGE = {
  'Diagnostic à domicile': QUOTE_PROGRESS_STAGES.diagnostic,
  'Comparaison devis / rapport': QUOTE_PROGRESS_STAGES.projectValidation,
  'Validation par le bénéficiaire': QUOTE_PROGRESS_STAGES.projectValidation,
  'Instruction financière': QUOTE_PROGRESS_STAGES.funding,
  'Suivi de fin de projet': QUOTE_PROGRESS_STAGES.projectFollowUp,
  'Fin de l’accompagnement': QUOTE_PROGRESS_STAGES.completion,
  "Fin de l'accompagnement": QUOTE_PROGRESS_STAGES.completion,
};

const AMO_C_STEP_TO_STAGE_INDEX = [0, 1, 1, 2, 3, 4];
const AMO_AD_STEP_TO_STAGE_INDEX = [0, 1, 1, 2];

const STATUS_COLORS = {
  green: '#B33131',
  orange: '#9A3F00',
  red: '#B33131',
  gray: '#5A5A5A',
  black: '#000000',
};

const STATUS_TINTS = {
  green: 'rgba(179, 49, 49, 0.12)',
  orange: 'rgba(250, 139, 65, 0.13)',
  red: 'rgba(249, 97, 97, 0.12)',
  gray: 'rgba(152, 157, 163, 0.12)',
  black: 'rgba(0, 0, 0, 0.08)',
};

const STATUS_GLOWS = {
  green: 'rgba(179, 49, 49, 0.34)',
  orange: 'rgba(250, 139, 65, 0.34)',
  red: 'rgba(249, 97, 97, 0.32)',
  gray: 'rgba(152, 157, 163, 0.3)',
  black: 'rgba(0, 0, 0, 0.24)',
};

const FLOW_GLOWS = {
  green: 'rgba(179, 49, 49, 0.52)',
  orange: 'rgba(250, 139, 65, 0.5)',
  red: 'rgba(249, 97, 97, 0.48)',
  gray: 'rgba(112, 120, 128, 0.44)',
  black: 'rgba(35, 40, 45, 0.4)',
};

const FLOW_SEGMENT_DURATION = 1100;
const FLOW_START_DELAY = 580;
const CURRENT_BREATH_DURATION = 2600;
const CURRENT_BREATH_DELAY = 760;

const revealJourney = keyframes`
  0% {
    opacity: 0;
    transform: translateY(18px) scale(0.985);
  }

  65% {
    opacity: 1;
    transform: translateY(-2px) scale(1);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const revealStep = keyframes`
  0% {
    opacity: 0;
    transform: translateY(18px) scale(0.9);
  }

  72% {
    opacity: 1;
    transform: translateY(-3px) scale(1.02);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const pulseCurrent = keyframes`
  0%, 100% {
    opacity: 0.1;
    transform: translateY(0) scale(0.94);
  }

  50% {
    opacity: 0.34;
    transform: translateY(-3px) scale(1.48);
  }
`;

const breatheCurrentHalo = keyframes`
  0%, 100% {
    opacity: 0.26;
    transform: translateY(0) scale(0.98);
  }

  50% {
    opacity: 0.72;
    transform: translateY(-3px) scale(1.2);
  }
`;

const pulseReached = keyframes`
  0%, 58%, 100% {
    opacity: 0;
    transform: scale(0.88);
  }

  66% {
    opacity: 0.38;
    transform: scale(1);
  }

  82% {
    opacity: 0;
    transform: scale(1.46);
  }
`;

const breatheCurrent = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
    filter: brightness(1);
  }

  50% {
    transform: scale(1.025);
    filter: brightness(1.08);
  }
`;

const drawAcross = keyframes`
  0% {
    transform: scaleX(0);
  }

  100% {
    transform: scaleX(1);
  }
`;

const drawDown = keyframes`
  0% {
    transform: scaleY(0);
  }

  100% {
    transform: scaleY(1);
  }
`;

const createStreamAcross = activeWindow => keyframes`
  0% {
    opacity: 0;
    left: 5px;
    transform: scaleX(0.72);
    transform-origin: right center;
  }

  ${activeWindow * 0.05}% {
    opacity: 1;
    left: 5px;
    transform: scaleX(1);
  }

  ${activeWindow * 0.78}% {
    opacity: 1;
    left: calc(100% - 28px);
    transform: scaleX(1);
  }

  ${activeWindow * 0.92}% {
    opacity: 0.72;
    left: calc(100% - 8px);
    transform: scaleX(0.72);
  }

  ${activeWindow}% {
    opacity: 0;
    left: 100%;
    transform: scaleX(0.16);
  }

  100% {
    opacity: 0;
    left: 100%;
    transform: scaleX(0.16);
  }
`;

const createStreamDown = activeWindow => keyframes`
  0% {
    opacity: 0;
    top: 5px;
    transform: scaleY(0.72);
    transform-origin: center bottom;
  }

  ${activeWindow * 0.05}% {
    opacity: 1;
    top: 5px;
    transform: scaleY(1);
  }

  ${activeWindow * 0.78}% {
    opacity: 1;
    top: calc(100% - 28px);
    transform: scaleY(1);
  }

  ${activeWindow * 0.92}% {
    opacity: 0.72;
    top: calc(100% - 8px);
    transform: scaleY(0.72);
  }

  ${activeWindow}% {
    opacity: 0;
    top: 100%;
    transform: scaleY(0.16);
  }

  100% {
    opacity: 0;
    top: 100%;
    transform: scaleY(0.16);
  }
`;

const markerArrival = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.4) rotate(-14deg);
  }

  64% {
    opacity: 1;
    transform: scale(1.16) rotate(3deg);
  }

  82% {
    transform: scale(0.96) rotate(0deg);
  }

  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
`;

const rotateCurrentRing = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const sweepLiquidGlass = keyframes`
  0%, 18% {
    opacity: 0;
    transform: translate3d(-160%, 0, 0) skewX(-16deg);
  }

  42% {
    opacity: 0.58;
  }

  68%, 100% {
    opacity: 0;
    transform: translate3d(360%, 0, 0) skewX(-16deg);
  }
`;

const sweepHeader = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-120%) skewX(-18deg);
  }

  25% {
    opacity: 0.7;
  }

  65%, 100% {
    opacity: 0;
    transform: translateX(220%) skewX(-18deg);
  }
`;

const normalizeLabel = value =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const asNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const isAmoAdValue = value => {
  if (typeof value !== 'string') return null;

  const normalized = normalizeLabel(value).replace(/[^a-z0-9]/g, '');
  if (normalized.includes('amoad') || normalized === 'ad') return true;
  if (normalized.includes('amoc') || normalized === 'c') return false;
  return null;
};

const getStages = ({ progressTotal, folderType, amoType, variant }) => {
  const explicitVariant = [amoType, folderType, variant]
    .map(isAmoAdValue)
    .find(value => value !== null);
  const isAmoAd = explicitVariant === undefined ? asNumber(progressTotal) === 4 : explicitVariant;

  return isAmoAd ? AMO_AD_STAGES : AMO_C_STAGES;
};

const stageIndexFromStep = (step, isAmoAd) => {
  const steps = isAmoAd ? AMO_AD_STEP_TO_STAGE_INDEX : AMO_C_STEP_TO_STAGE_INDEX;
  const numericStep = asNumber(step);

  if (!numericStep || numericStep < 1 || numericStep > steps.length) return -1;
  return steps[numericStep - 1];
};

const stageIndexFromLabel = (label, stages) => {
  if (label === undefined || label === null) return -1;

  const normalizedLabel = normalizeLabel(label);
  const directIndex = stages.findIndex(stage => normalizeLabel(stage) === normalizedLabel);
  if (directIndex >= 0) return directIndex;

  const displayStage = Object.keys(CALCULATED_STATE_TO_STAGE).find(
    calculatedState => normalizeLabel(calculatedState) === normalizedLabel,
  );
  if (!displayStage) return -1;

  return stages.findIndex(stage => stage === CALCULATED_STATE_TO_STAGE[displayStage]);
};

const getCurrentStageIndex = ({ currentStage, currentStep, progressCurrent, stages }) => {
  const directIndex = stageIndexFromLabel(currentStage, stages);
  if (directIndex >= 0) return directIndex;

  let step = currentStep;
  if (step === undefined) step = progressCurrent;
  if (step === undefined) step = asNumber(currentStage);
  return stageIndexFromStep(step, stages.length === AMO_AD_STAGES.length);
};

export const getVisibleQuoteProgress = quote => {
  const stages = getStages(quote);
  const currentIndex = getCurrentStageIndex({
    currentStage: quote.displayStage || quote.calculatedState,
    progressCurrent: quote.progressCurrent,
    stages,
  });
  return { current: currentIndex + 1, total: stages.length };
};

const getSelection = (selection, stages, currentStageIndex, disabledStages = []) => {
  const selectionIndex = stageIndexFromLabel(selection, stages);
  if (
    selectionIndex >= 0 &&
    selectionIndex <= currentStageIndex &&
    !disabledStages.includes(stages[selectionIndex])
  ) {
    return stages[selectionIndex];
  }

  return [...stages]
    .slice(0, currentStageIndex + 1)
    .reverse()
    .find(stage => !disabledStages.includes(stage));
};

const stageTitle = ({ stage, isBlocked, isFuture }) => {
  if (isBlocked) return 'Cette étape est indisponible pour un devis écarté';
  if (isFuture) return 'Cette étape sera accessible plus tard';
  return stage;
};

const stageStatus = ({ isBlocked, isCompleted, isCurrent }) => {
  if (isBlocked) return 'Indisponible';
  if (isCompleted) return 'Terminée';
  if (isCurrent) return 'En cours';
  return 'À venir';
};

const getDetailsForStage = (details, stage, stageIndex, context) => {
  if (details === undefined || details === null) return null;
  if (typeof details === 'function') return details(stage, stageIndex, context);
  if (Array.isArray(details)) return details[stageIndex];
  if (typeof details !== 'object' || React.isValidElement(details)) return details;
  if (Object.prototype.hasOwnProperty.call(details, stage)) return details[stage];

  const matchingKey = Object.keys(details).find(
    key => normalizeLabel(key) === normalizeLabel(stage),
  );
  return matchingKey ? details[matchingKey] : null;
};

const getDetailKey = value => {
  if (React.isValidElement(value) && value.key !== null) return value.key;
  if (value && typeof value === 'object') return Object.keys(value).join('-');
  return String(value);
};

const getSuppliedDetails = ({ phaseDetails, stageDetails, detailsByStage, details }) => {
  if (phaseDetails !== undefined) return phaseDetails;
  if (stageDetails !== undefined) return stageDetails;
  if (detailsByStage !== undefined) return detailsByStage;
  return details;
};

const getPhaseDetail = ({
  renderPhaseDetails,
  renderDetails,
  suppliedDetails,
  selected,
  selectedIndex,
  detailsContext,
}) => {
  if (typeof renderPhaseDetails === 'function') {
    return renderPhaseDetails(selected, selectedIndex, detailsContext);
  }
  if (typeof renderDetails === 'function') {
    return renderDetails(selected, selectedIndex, detailsContext);
  }
  return getDetailsForStage(suppliedDetails, selected, selectedIndex, detailsContext);
};

const stageMetadataLabel = metadata => {
  if (metadata === undefined || metadata === null || metadata === false) return null;
  if (React.isValidElement(metadata)) return metadata;
  if (typeof metadata !== 'object') return String(metadata);

  if (metadata.label && metadata.value) return `${metadata.label} · ${metadata.value}`;
  if (metadata.value) return String(metadata.value);
  if (metadata.label) return String(metadata.label);
  return null;
};

const renderDetailValue = (value, keyPrefix) => {
  if (value === undefined || value === null || value === false) return null;
  if (React.isValidElement(value)) return value;

  if (Array.isArray(value)) {
    return value.map(item => (
      <Box key={`${keyPrefix}-${getDetailKey(item)}`}>
        {renderDetailValue(item, `${keyPrefix}-${getDetailKey(item)}`)}
      </Box>
    ));
  }

  if (typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'label')) {
      return (
        <Flex key={keyPrefix} alignItems="baseline" flexWrap="wrap" gridGap={1}>
          <Text fontWeight="semibold">{value.label}</Text>
          {value.value !== undefined && (
            <Box>{renderDetailValue(value.value, `${keyPrefix}-value`)}</Box>
          )}
        </Flex>
      );
    }

    if (Object.prototype.hasOwnProperty.call(value, 'title')) {
      return (
        <Box key={keyPrefix}>
          <Text fontWeight="semibold">{value.title}</Text>
          {value.description !== undefined &&
            renderDetailValue(value.description, `${keyPrefix}-description`)}
          {value.content !== undefined && renderDetailValue(value.content, `${keyPrefix}-content`)}
          {value.items !== undefined && renderDetailValue(value.items, `${keyPrefix}-items`)}
        </Box>
      );
    }

    return Object.keys(value).map(key => (
      <Flex key={`${keyPrefix}-${key}`} alignItems="baseline" flexWrap="wrap" gridGap={1}>
        <Text fontWeight="semibold">{key}</Text>
        <Box>{renderDetailValue(value[key], `${keyPrefix}-${key}`)}</Box>
      </Flex>
    ));
  }

  return <Text>{String(value)}</Text>;
};

export const QuoteProgressTimeline = ({
  currentStage,
  currentStep,
  progressCurrent,
  progressTotal,
  folderType,
  amoType,
  variant,
  selectedStage,
  activeStage,
  statusColor = 'green',
  disabledStages = EMPTY_DISABLED_STAGES,
  onSelect,
  onStageSelect,
  onStageChange,
  onPhaseChange,
  phaseDetails,
  stageDetails,
  detailsByStage,
  details,
  stageMetadata,
  detailsId,
  renderPhaseDetails,
  renderDetails,
}) => {
  const stages = getStages({ progressTotal, folderType, amoType, variant });
  const isAmoAd = stages === AMO_AD_STAGES;
  const currentStageIndex = getCurrentStageIndex({
    currentStage,
    currentStep,
    progressCurrent,
    stages,
  });
  const selectionProp = selectedStage === undefined ? activeStage : selectedStage;
  const hasSelectionProp =
    selectionProp !== undefined && selectionProp !== null && selectionProp !== '';
  const defaultSelection = currentStageIndex >= 0 ? stages[currentStageIndex] : stages[0];
  const [internalSelection, setInternalSelection] = useState(defaultSelection);

  useEffect(() => {
    if (hasSelectionProp) {
      setInternalSelection(
        getSelection(selectionProp, stages, Math.max(currentStageIndex, 0), disabledStages),
      );
    } else if (currentStageIndex >= 0) {
      setInternalSelection(
        getSelection(stages[currentStageIndex], stages, currentStageIndex, disabledStages),
      );
    }
  }, [currentStageIndex, disabledStages, hasSelectionProp, selectionProp, stages]);

  const flowChoreography = useMemo(() => {
    const connectorCount = Math.max(currentStageIndex, 1);
    const activeWindow = 100 / connectorCount;

    return {
      across: createStreamAcross(activeWindow),
      down: createStreamDown(activeWindow),
      duration: connectorCount * FLOW_SEGMENT_DURATION,
    };
  }, [currentStageIndex]);

  if (currentStageIndex < 0) return null;

  const selected = getSelection(
    hasSelectionProp ? selectionProp : internalSelection,
    stages,
    currentStageIndex,
    disabledStages,
  );
  const selectedIndex = stages.indexOf(selected);
  const progressColor = STATUS_COLORS[statusColor] || STATUS_COLORS.green;
  const progressTint = STATUS_TINTS[statusColor] || STATUS_TINTS.green;
  const progressGlow = STATUS_GLOWS[statusColor] || STATUS_GLOWS.green;
  const flowGlow = FLOW_GLOWS[statusColor] || FLOW_GLOWS.green;
  const selectionHandler = onSelect || onStageSelect || onStageChange || onPhaseChange;
  const suppliedDetails = getSuppliedDetails({
    phaseDetails,
    stageDetails,
    detailsByStage,
    details,
  });
  const detailsContext = {
    currentStage,
    currentStageIndex,
    currentStep: currentStep === undefined ? progressCurrent : currentStep,
    progressTotal,
    isAmoAd,
  };
  const phaseDetail = getPhaseDetail({
    renderPhaseDetails,
    renderDetails,
    suppliedDetails,
    selected,
    selectedIndex,
    detailsContext,
  });
  const hasPhaseDetail = phaseDetail !== undefined && phaseDetail !== null && phaseDetail !== false;

  const handleSelect = stage => {
    const index = stages.indexOf(stage);
    if (index < 0 || index > currentStageIndex || disabledStages.includes(stage)) return;

    setInternalSelection(stage);
    if (typeof selectionHandler === 'function') selectionHandler(stage);
  };

  const handleGlassPointerMove = event => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const x = Math.max(0, Math.min(100, ((event.clientX - bounds.left) / bounds.width) * 100));
    const y = Math.max(0, Math.min(100, ((event.clientY - bounds.top) / bounds.height) * 100));
    event.currentTarget.style.setProperty('--glass-x', `${x.toFixed(1)}%`);
    event.currentTarget.style.setProperty('--glass-y', `${y.toFixed(1)}%`);
  };

  const resetGlassPointer = event => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    event.currentTarget.style.setProperty('--glass-x', '50%');
    event.currentTarget.style.setProperty('--glass-y', '18%');
  };

  return (
    <Box
      as="section"
      data-testid="quote-progress-timeline"
      data-progress-total={progressTotal}
      data-amo-type={isAmoAd ? 'AMOad' : 'AMOc'}
      width="100%"
      css={{
        animation: `${revealJourney} 620ms cubic-bezier(0.16, 1, 0.3, 1) both`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Flex
        alignItems={{ base: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        flexDirection={{ base: 'column', md: 'row' }}
        gridGap={4}
        bg={`linear-gradient(135deg, white 0%, white 58%, ${progressTint} 100%)`}
        border="1px solid"
        borderColor="lightGray3"
        roundedTop="lg"
        px={{ base: 4, md: 5 }}
        py={4}
        boxShadow={`0 12px 32px rgba(46, 73, 112, 0.1), 0 0 0 1px ${progressTint}`}
        position="relative"
        overflow="hidden"
      >
        <Box
          aria-hidden="true"
          position="absolute"
          top="-20%"
          bottom="-20%"
          left="0"
          width="26%"
          bg="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.9), transparent)"
          pointerEvents="none"
          data-motion="header-sweep"
          css={{
            animation: `${sweepHeader} 1.35s 180ms cubic-bezier(0.16, 1, 0.3, 1) both`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
        <Flex alignItems="center" gridGap={3} position="relative" zIndex="1">
          <Flex
            w="42px"
            h="42px"
            flexShrink={0}
            rounded="full"
            alignItems="center"
            justifyContent="center"
            bg="lightGray"
            color="coral.900"
            data-testid="journey-icon"
          >
            <Icon name="folder" size={5} />
          </Flex>
          <Box>
            <Text fontFamily="heading" fontSize="md" fontWeight="bold" color="semiDarkGray.900">
              Parcours du dossier
            </Text>
            <Text mt={1} color="semiDarkGray.900" fontSize="xs">
              {isAmoAd ? 'Parcours AMO AD' : 'Parcours AMO C'}
            </Text>
          </Box>
        </Flex>
        <Flex alignItems="center" position="relative" zIndex="1">
          <Box bg={progressColor} color="white" rounded="full" px={3} py={1}>
            <Text fontSize="xs" fontWeight="bold">
              Étape {currentStageIndex + 1} sur {stages.length}
            </Text>
          </Box>
        </Flex>
      </Flex>
      <Box
        as="nav"
        aria-label="Progression du dossier"
        bg="white"
        border="1px solid"
        borderTop="0"
        borderColor="lightGray3"
        roundedBottom="lg"
        px={{ base: 3, md: 4 }}
        py={{ base: 4, md: 5 }}
        position="relative"
        overflow="hidden"
        data-motion="journey-choreography"
        data-hover-feedback="journey"
        data-flow-mode="synchronized"
        css={{
          '&:hover [data-motion="connector-flow"], &:focus-within [data-motion="connector-flow"]': {
            filter: 'saturate(1.1) brightness(1.2) drop-shadow(0 0 2px rgba(255, 255, 255, 0.84))',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& [data-motion="connector-flow"]': {
              animation: 'none',
            },
          },
        }}
      >
        <Flex
          as="ol"
          listStyleType="none"
          m={0}
          flexDirection={{ base: 'column', md: 'row' }}
          alignItems="stretch"
          width="100%"
          position="relative"
          zIndex="1"
        >
          {selectedIndex >= 0 && (
            <Box
              as="li"
              aria-hidden="true"
              display={{ base: 'none', md: 'block' }}
              position="absolute"
              top="0"
              bottom="0"
              left="4px"
              width={`calc(100% / ${stages.length} - 8px)`}
              rounded="lg"
              bg="darkGray"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.22)"
              boxShadow="0 12px 24px rgba(35, 40, 45, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.16)"
              transform={`translateX(calc(${selectedIndex} * (100% + 8px)))`}
              transition="transform 620ms cubic-bezier(0.22, 1, 0.36, 1)"
              pointerEvents="none"
              data-testid="timeline-selection-surface"
              data-selected-index={selectedIndex}
              css={{
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            />
          )}
          {stages.map((stage, index) => {
            const isCompleted = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;
            const isFuture = index > currentStageIndex;
            const isBlocked = disabledStages.includes(stage);
            const isSelected = index === selectedIndex;
            const hasNextStage = index < stages.length - 1;
            const isReachedConnector = index < currentStageIndex;
            const isActiveConnector = isReachedConnector && index === currentStageIndex - 1;
            const isBlockedConnector = disabledStages.includes(stages[index + 1]);
            const metadata = stageMetadataLabel(
              getDetailsForStage(stageMetadata, stage, index, detailsContext),
            );
            const status = stageStatus({ isBlocked, isCompleted, isCurrent });
            let labelColor = 'semiDarkGray.900';
            if (isFuture || isBlocked) labelColor = 'semiDarkGray.600';
            if (isSelected) labelColor = 'white';
            let markerColor = progressColor;
            if (isFuture || isBlocked) markerColor = 'white';
            const markerTextColor = isFuture || isBlocked ? 'semiDarkGray.600' : 'white';
            let stageState = 'future';
            if (isCompleted) stageState = 'completed';
            if (isCurrent) stageState = 'current';
            if (isBlocked) stageState = 'blocked';
            let markerBorderColor = progressColor;
            if (isFuture || isBlocked) markerBorderColor = 'lightGray3';
            let stageStatusColor = progressColor;
            if (isFuture || isBlocked) stageStatusColor = 'semiDarkGray.600';
            if (isSelected) stageStatusColor = 'white';
            let markerBoxShadow = 'none';
            if (isCompleted) markerBoxShadow = `0 6px 18px ${progressGlow}`;
            if (isCurrent) {
              markerBoxShadow = `0 0 0 4px white, 0 10px 28px ${progressGlow}`;
            }
            const stageRevealDelay = 180 + index * 125;
            const connectorDrawDelay = 360 + index * 180;
            const markerAnimation = isCurrent
              ? `${markerArrival} 680ms ${stageRevealDelay}ms cubic-bezier(0.34, 1.56, 0.64, 1) both, ${breatheCurrent} ${CURRENT_BREATH_DURATION}ms ${stageRevealDelay +
                  CURRENT_BREATH_DELAY}ms ease-in-out infinite`
              : `${markerArrival} 680ms ${stageRevealDelay}ms cubic-bezier(0.34, 1.56, 0.64, 1) both`;

            return (
              <Box
                as="li"
                key={stage}
                flex="1"
                minW={0}
                mt={{ base: index === 0 ? 0 : 3, md: 0 }}
                px={{ base: 0, md: 1 }}
                position="relative"
                zIndex="1"
                data-motion="timeline-reveal"
                css={{
                  animation: `${revealStep} 620ms ${stageRevealDelay}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                  ...(!isFuture && !isBlocked
                    ? {
                        '&:hover [data-stage-marker-shell], &:focus-within [data-stage-marker-shell]': {
                          transform: 'scale(1.07)',
                          filter: `drop-shadow(0 9px 12px ${progressGlow})`,
                        },
                        '&:hover [data-stage-hover-ring], &:focus-within [data-stage-hover-ring]': {
                          opacity: 0.62,
                          transform: 'scale(1)',
                        },
                        '&:hover [data-stage-status], &:focus-within [data-stage-status]': {
                          backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.16)' : progressTint,
                          boxShadow: `0 0 0 1px ${progressGlow}`,
                        },
                        '&:hover > [data-motion="completed-flow"] [data-motion="connector-flow"], &:focus-within > [data-motion="completed-flow"] [data-motion="connector-flow"]': {
                          filter:
                            'saturate(1.14) brightness(1.28) drop-shadow(0 0 2px rgba(255, 255, 255, 0.9))',
                        },
                        '&:hover > [data-motion="completed-flow"] [data-motion="connector-track"], &:focus-within > [data-motion="completed-flow"] [data-motion="connector-track"]': {
                          boxShadow: `0 0 16px ${flowGlow}`,
                        },
                      }
                    : {}),
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }}
              >
                {hasNextStage && (
                  <Box
                    aria-hidden="true"
                    data-testid={`timeline-connector-${index + 1}`}
                    data-connector-state={isReachedConnector ? 'completed' : 'future'}
                    data-motion={isReachedConnector ? 'completed-flow' : undefined}
                    position="absolute"
                    zIndex="0"
                    top={{ base: '30px', md: '33px' }}
                    left={{ base: '28px', md: '50%' }}
                    width={{ base: '6px', md: '100%' }}
                    height={{ base: 'calc(100% + 12px)', md: '6px' }}
                    pointerEvents="none"
                  >
                    <Box
                      position="absolute"
                      top="0"
                      right="0"
                      bottom="0"
                      left="0"
                      bg="lightGray3"
                      rounded="full"
                      overflow="hidden"
                      data-motion="connector-track"
                      transition="box-shadow 220ms ease"
                      boxShadow={
                        isReachedConnector && !isBlockedConnector
                          ? `0 0 ${isActiveConnector ? '14px' : '10px'} ${flowGlow}`
                          : 'inset 0 1px 2px rgba(35, 40, 45, 0.08)'
                      }
                    >
                      {isReachedConnector && !isBlockedConnector && (
                        <React.Fragment>
                          <Box
                            position="absolute"
                            top="0"
                            right="0"
                            bottom="0"
                            left="0"
                            bg={progressColor}
                            transformOrigin={{ base: 'center top', md: 'left center' }}
                            rounded="full"
                            data-motion="connector-draw"
                            css={{
                              animation: `${drawDown} 760ms ${connectorDrawDelay}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                              '@media (min-width: 48em)': {
                                animation: `${drawAcross} 760ms ${connectorDrawDelay}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                              },
                              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                            }}
                          />
                          <Box
                            position="absolute"
                            zIndex="1"
                            top={{ base: '0', md: '1px' }}
                            left={{ base: '1px', md: '0' }}
                            width={{ base: '1px', md: '100%' }}
                            height={{ base: '100%', md: '1px' }}
                            bg="rgba(255, 255, 255, 0.28)"
                            pointerEvents="none"
                            data-motion="connector-rail-sheen"
                          />
                        </React.Fragment>
                      )}
                    </Box>
                    {isReachedConnector && !isBlockedConnector && (
                      <Box
                        position="absolute"
                        zIndex="1"
                        top="0"
                        right="0"
                        bottom="0"
                        left="0"
                        pointerEvents="none"
                        data-motion="connector-flow-viewport"
                        css={{
                          clipPath: 'inset(0 -12px 0 -12px)',
                          '@media (min-width: 48em)': {
                            clipPath: 'inset(-12px 0 -12px 0)',
                          },
                        }}
                      >
                        <Box
                          position="absolute"
                          top={{ base: '5px', md: '50%' }}
                          left={{ base: '50%', md: '5px' }}
                          width="0"
                          height="0"
                          opacity="0"
                          data-motion="connector-flow"
                          data-flow-shape="light-ribbon"
                          data-flow-arrival="absorbed"
                          data-flow-strength={isActiveConnector ? 'active' : 'steady'}
                          data-flow-cycle={flowChoreography.duration}
                          data-flow-delay={FLOW_START_DELAY + index * FLOW_SEGMENT_DURATION}
                          css={{
                            animation: `${flowChoreography.down} ${
                              flowChoreography.duration
                            }ms ${FLOW_START_DELAY +
                              index * FLOW_SEGMENT_DURATION}ms linear infinite both`,
                            willChange: 'top, left, opacity, transform',
                            '@media (min-width: 48em)': {
                              animation: `${flowChoreography.across} ${
                                flowChoreography.duration
                              }ms ${FLOW_START_DELAY +
                                index * FLOW_SEGMENT_DURATION}ms linear infinite both`,
                            },
                            '@media (prefers-reduced-motion: reduce)': {
                              animation: 'none',
                              opacity: 0,
                            },
                          }}
                        >
                          <Box
                            position="absolute"
                            zIndex="0"
                            right={{ base: 'auto', md: '0' }}
                            bottom={{ base: '0', md: 'auto' }}
                            top={{ base: 'auto', md: '-3px' }}
                            left={{ base: '-3px', md: 'auto' }}
                            width={{ base: '6px', md: '64px' }}
                            height={{ base: '64px', md: '6px' }}
                            rounded="full"
                            boxShadow={`0 0 9px rgba(255, 255, 255, 0.72), 0 0 14px ${flowGlow}`}
                            data-motion="connector-flow-ribbon"
                            css={{
                              backgroundImage:
                                'linear-gradient(180deg, transparent 0%, rgba(255, 255, 255, 0.18) 10%, rgba(255, 255, 255, 0.72) 30%, white 50%, rgba(255, 255, 255, 0.72) 70%, rgba(255, 255, 255, 0.18) 90%, transparent 100%)',
                              '@media (min-width: 48em)': {
                                backgroundImage:
                                  'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.18) 10%, rgba(255, 255, 255, 0.72) 30%, white 50%, rgba(255, 255, 255, 0.72) 70%, rgba(255, 255, 255, 0.18) 90%, transparent 100%)',
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
                <Flex
                  as="button"
                  type="button"
                  disabled={isFuture || isBlocked}
                  aria-disabled={isFuture || isBlocked}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-pressed={isSelected}
                  aria-controls={!isFuture && !isBlocked ? detailsId : undefined}
                  aria-expanded={!isFuture && !isBlocked ? isSelected : undefined}
                  aria-label={`${stage}${isCurrent ? ', phase en cours' : ''}`}
                  title={stageTitle({ stage, isBlocked, isFuture })}
                  data-stage={stage}
                  data-stage-state={stageState}
                  data-selected-surface={isSelected ? 'liquid-glass' : undefined}
                  data-glass-tone={isSelected ? 'graphite' : undefined}
                  data-hover-feedback={isFuture || isBlocked ? 'disabled' : 'enabled'}
                  position="relative"
                  zIndex="1"
                  width="100%"
                  minH={{ base: '90px', md: '184px' }}
                  height="100%"
                  px={{ base: 2, md: 2 }}
                  py={{ base: 2, md: 3 }}
                  border="1px solid"
                  borderColor={isSelected ? 'rgba(255, 255, 255, 0.22)' : 'transparent'}
                  rounded="lg"
                  bg={{ base: isSelected ? 'darkGray' : 'transparent', md: 'transparent' }}
                  flexDirection={{ base: 'row', md: 'column' }}
                  alignItems="center"
                  cursor={isFuture || isBlocked ? 'not-allowed' : 'pointer'}
                  fontFamily="body"
                  textAlign={{ base: 'left', md: 'center' }}
                  onClick={() => handleSelect(stage)}
                  onMouseMove={isSelected ? handleGlassPointerMove : undefined}
                  onMouseLeave={isSelected ? resetGlassPointer : undefined}
                  _hover={
                    isFuture || isBlocked
                      ? {}
                      : {
                          borderColor: isSelected ? 'rgba(255, 255, 255, 0.38)' : progressTint,
                          boxShadow: `inset 0 0 0 1px ${
                            isSelected ? 'rgba(255, 255, 255, 0.1)' : progressTint
                          }`,
                        }
                  }
                  _active={
                    isFuture || isBlocked
                      ? {}
                      : {
                          filter: 'brightness(0.99)',
                        }
                  }
                  _focus={{ outline: 'none', boxShadow: 'outline' }}
                  transition="box-shadow 220ms ease, background-color 220ms ease, border-color 220ms ease, filter 220ms ease"
                  data-motion={isSelected ? 'stage-focus' : undefined}
                  css={{
                    '--glass-x': '50%',
                    '--glass-y': '18%',
                    isolation: 'isolate',
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                      transition: 'none',
                    },
                  }}
                >
                  {isSelected && (
                    <Box
                      aria-hidden="true"
                      position="absolute"
                      zIndex="0"
                      top="0"
                      right="0"
                      bottom="0"
                      left="0"
                      rounded="lg"
                      overflow="hidden"
                      pointerEvents="none"
                      transition="transform 260ms cubic-bezier(0.16, 1, 0.3, 1), border-radius 320ms cubic-bezier(0.16, 1, 0.3, 1)"
                      data-motion="selected-liquid-glass"
                    >
                      <Box
                        position="absolute"
                        top="0"
                        right="0"
                        bottom="0"
                        left="0"
                        pointerEvents="none"
                        data-motion="selected-liquid-refraction"
                        css={{
                          background:
                            'linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.08) 50%, transparent 70%)',
                          backgroundSize: '200% 200%',
                          backgroundPosition: 'var(--glass-x, 50%) var(--glass-y, 18%)',
                        }}
                      />
                      <Box
                        position="absolute"
                        top="1px"
                        right="1px"
                        bottom="1px"
                        left="1px"
                        rounded="lg"
                        border="1px solid"
                        borderColor="rgba(255, 255, 255, 0.12)"
                        boxShadow="inset 0 1px 0 rgba(255, 255, 255, 0.14)"
                        pointerEvents="none"
                        data-motion="selected-liquid-rim"
                      />
                      <Box
                        position="absolute"
                        top="-24%"
                        bottom="-24%"
                        left="-42%"
                        width="24%"
                        bg="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)"
                        filter="blur(3px)"
                        pointerEvents="none"
                        data-motion="selected-liquid-highlight"
                        css={{
                          animation: `${sweepLiquidGlass} 5.8s 900ms cubic-bezier(0.45, 0.05, 0.2, 1) infinite`,
                          '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                            opacity: 0,
                          },
                        }}
                      />
                    </Box>
                  )}
                  <Box
                    position="relative"
                    flexShrink={0}
                    zIndex="1"
                    transition="transform 280ms cubic-bezier(0.16, 1, 0.3, 1), filter 220ms ease"
                    data-stage-marker-shell="true"
                  >
                    {!isFuture && !isBlocked && (
                      <Box
                        aria-hidden="true"
                        position="absolute"
                        top="-7px"
                        right="-7px"
                        bottom="-7px"
                        left="-7px"
                        rounded="full"
                        border="1px solid"
                        borderColor={progressColor}
                        opacity="0"
                        transform="scale(0.72)"
                        transition="opacity 200ms ease, transform 320ms cubic-bezier(0.16, 1, 0.3, 1)"
                        pointerEvents="none"
                        data-stage-hover-ring="true"
                      />
                    )}
                    {isCompleted && !isBlocked && (
                      <Box
                        aria-hidden="true"
                        position="absolute"
                        top="0"
                        right="0"
                        bottom="0"
                        left="0"
                        rounded="full"
                        border="2px solid"
                        borderColor={progressColor}
                        boxShadow={`0 0 16px ${progressGlow}`}
                        data-motion="reached-pulse"
                        css={{
                          animation: `${pulseReached} 3.2s ${stageRevealDelay +
                            820}ms ease-out infinite`,
                          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                        }}
                      />
                    )}
                    {isCurrent && !isBlocked && (
                      <React.Fragment>
                        <Box
                          aria-hidden="true"
                          position="absolute"
                          top="-10px"
                          right="-10px"
                          bottom="-10px"
                          left="-10px"
                          rounded="full"
                          border="2px solid transparent"
                          borderTopColor={isSelected ? 'whiteAlpha.800' : progressColor}
                          borderBottomColor={isSelected ? 'whiteAlpha.300' : progressTint}
                          pointerEvents="none"
                          data-motion="current-orbit"
                          css={{
                            animation: `${rotateCurrentRing} 8s linear infinite`,
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                          }}
                        />
                        <Box
                          aria-hidden="true"
                          position="absolute"
                          top="0"
                          right="0"
                          bottom="0"
                          left="0"
                          rounded="full"
                          bg={progressColor}
                          boxShadow={`0 0 16px ${progressGlow}`}
                          data-motion="current-pulse"
                          data-sync-group="current-breath"
                          data-motion-cycle={CURRENT_BREATH_DURATION}
                          css={{
                            animation: `${pulseCurrent} ${CURRENT_BREATH_DURATION}ms ${stageRevealDelay +
                              CURRENT_BREATH_DELAY}ms ease-in-out infinite`,
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                          }}
                        />
                        <Box
                          aria-hidden="true"
                          position="absolute"
                          top="0"
                          right="0"
                          bottom="0"
                          left="0"
                          rounded="full"
                          border="1px solid"
                          borderColor={progressColor}
                          boxShadow={`0 0 14px ${progressGlow}`}
                          data-motion="current-halo"
                          data-sync-group="current-breath"
                          data-motion-cycle={CURRENT_BREATH_DURATION}
                          css={{
                            animation: `${breatheCurrentHalo} ${CURRENT_BREATH_DURATION}ms ${stageRevealDelay +
                              CURRENT_BREATH_DELAY}ms ease-in-out infinite`,
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                          }}
                        />
                      </React.Fragment>
                    )}
                    <Flex
                      position="relative"
                      w="44px"
                      h="44px"
                      rounded="full"
                      alignItems="center"
                      justifyContent="center"
                      bg={markerColor}
                      color={markerTextColor}
                      border="3px solid"
                      borderColor={markerBorderColor}
                      boxShadow={markerBoxShadow}
                      fontWeight="bold"
                      data-testid={isSelected ? 'selected-stage-marker' : undefined}
                      data-motion="milestone-arrival"
                      data-sync-group={isCurrent ? 'current-breath' : undefined}
                      data-motion-cycle={isCurrent ? CURRENT_BREATH_DURATION : undefined}
                      css={{
                        animation: markerAnimation,
                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                      }}
                    >
                      {isCompleted ? <Icon name="tick" size={4} /> : index + 1}
                    </Flex>
                  </Box>
                  <Box
                    ml={{ base: 3, md: 0 }}
                    mt={{ base: 0, md: 3 }}
                    minW={0}
                    position="relative"
                    zIndex="1"
                  >
                    <Text
                      as="span"
                      d="inline-block"
                      px={2}
                      py="2px"
                      rounded="full"
                      bg={isSelected ? 'whiteAlpha.200' : 'white'}
                      color={stageStatusColor}
                      fontSize="10px"
                      fontWeight="bold"
                      letterSpacing="0"
                      textTransform="uppercase"
                      transition="background-color 180ms ease, box-shadow 180ms ease"
                      data-stage-status="true"
                    >
                      {status}
                    </Text>
                    <Text
                      mt={1}
                      maxW={{ base: 'none', md: '158px' }}
                      minH={{ base: 0, md: '54px' }}
                      fontSize="sm"
                      lineHeight="1.3"
                      fontWeight={isSelected || isCurrent ? 'bold' : 'semibold'}
                      color={labelColor}
                      transition="color 180ms ease"
                    >
                      {stage}
                    </Text>
                    {metadata && (
                      <Flex
                        mt={2}
                        alignItems="center"
                        justifyContent={{ base: 'flex-start', md: 'center' }}
                        color={isSelected ? 'whiteAlpha.900' : 'semiDarkGray.600'}
                        gridGap={1}
                      >
                        <Icon name="calendar" size={3} flexShrink={0} />
                        <Text fontSize="xs" lineHeight="1.25">
                          {metadata}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                </Flex>
              </Box>
            );
          })}
        </Flex>
      </Box>
      {hasPhaseDetail && (
        <Box
          data-testid="quote-progress-phase-details"
          role="region"
          aria-live="polite"
          aria-label={`Détails : ${selected}`}
          mt={2}
          px={{ base: 3, md: 5 }}
          py={4}
          border="1px solid"
          borderColor="lightGray3"
          borderLeft="4px solid"
          borderLeftColor="coral.900"
          rounded="md"
          bg="white"
          color="semiDarkGray.900"
          css={{
            animation: `${revealStep} 260ms ease-out both`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          <Text fontSize="sm" fontWeight="bold" color="coral.900" mb={2}>
            {selected}
          </Text>
          <Box fontSize="sm">{renderDetailValue(phaseDetail, 'phase-detail')}</Box>
        </Box>
      )}
    </Box>
  );
};
